import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Input, Select } from 'antd';
import { canManage, putWorkspaceMember, removeWorkspaceMember } from '../../services/WorkspacesService';
import { initialsOfName } from '../../utils/liveModel';
import { inkFor } from '../../utils/participantColors';
import { memberName, roleLabel } from '../../utils/workspaceDisplay';

const ROLES = [{ value: 'viewer', label: 'Viewer' }, { value: 'editor', label: 'Editor' }];
const OWNER_ROLE = { value: 'owner', label: 'Owner' };
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const lower = (value) => String(value || '').toLowerCase();

/** The name to show: the one the page knows (the workspace's owners, a project owner), else the email's local part. */
function displayName(person, workspace) {
  if (person?.name) { return person.name; }
  const known = memberName(person, workspace);
  if (known && lower(known) !== lower(person?.email) && known !== 'Someone') { return known; }
  return String(person?.email || '').split('@')[0] || 'Someone';
}

/** A round initials avatar in the person's presence colour (owner slot 0, then the members as listed). */
function MemberAvatar({ name, slot }) {
  return (
    <span
      aria-hidden="true"
      className="ws-avatar"
      style={{ '--ws-avatar-ink': inkFor(slot, false), '--ws-avatar-ink-dark': inkFor(slot, true) }}
    >
      {initialsOfName(name)}
    </span>
  );
}

MemberAvatar.propTypes = { name: PropTypes.string, slot: PropTypes.number.isRequired };

/**
 * A workspace's members, one bordered list with dividers: the owner first, then each member as listed. Each row has an
 * initials avatar, the name (else the email's local part) with the email under it and "You" for the person looking,
 * then a fixed right-hand area with the role and the row's action, so the roles line up in every row. The owner adds
 * people by email (viewer reads every project of the workspace, editor also changes them), changes roles and removes
 * them; a member can leave.
 */
export default function WorkspaceMembers({ workspace, user, onChange }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('viewer');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const owner = canManage(workspace);
  const me = lower(user?.email);

  const run = async(work) => {
    setBusy(true);
    setError(null);
    try {
      await work();
      await onChange();
      return true;
    } catch (failed) {
      setError(failed.message);
      return false;
    } finally {
      setBusy(false);
    }
  };
  const invite = async() => {
    if (await run(() => putWorkspaceMember(workspace.id, email, role))) { setEmail(''); }
  };

  const ownerPerson = workspace.owner || {};
  const ownerIsMe = Boolean((user?.sub && ownerPerson.sub === user.sub) || (me && lower(ownerPerson.email) === me));

  const who = (person, slot, isMe) => {
    const name = displayName(person, workspace);
    return (
      <>
        <MemberAvatar name={name} slot={slot} />
        <span className="ws-member-who">
          <span className="ws-member-name">
            <span className="ws-member-text" title={name}>{name}</span>
            {isMe && <span className="ws-chip ws-you">You</span>}
          </span>
          {person.email && <span className="ws-member-email" title={person.email}>{person.email}</span>}
        </span>
      </>
    );
  };

  return (
    <div className="workspace-section ws-members">
      {owner && (
        <div className="workspace-invite">
          <Input aria-label="Email" onChange={(event) => setEmail(event.target.value)} onPressEnter={() => EMAIL.test(email.trim()) && invite()} placeholder="name@company.com" value={email} />
          <Select aria-label="Role" onChange={setRole} options={ROLES} value={role} />
          <Button disabled={!EMAIL.test(email.trim())} loading={busy} onClick={invite} type="primary">Add</Button>
        </div>
      )}
      {error && <p className="sbx-action-error" role="alert">{error}</p>}
      <ul aria-label="Members" className="ws-member-list">
        <li className="ws-member">
          {who(ownerPerson, 0, ownerIsMe)}
          <span className="ws-member-role"><span className="ws-chip">Owner</span></span>
          <span className="ws-member-actions" />
        </li>
        {workspace.members.map((member, index) => {
          const isMe = lower(member.email) === me;
          return (
            <li className="ws-member" key={member.email}>
              {who(member, index + 1, isMe)}
              <span className="ws-member-role">
                {owner ? (
                  <Select
                    aria-label={`Role of ${member.email}`}
                    className="ws-member-select"
                    onChange={(next) => run(() => putWorkspaceMember(workspace.id, member.email, next))}
                    options={member.role === 'owner' ? [OWNER_ROLE, ...ROLES] : ROLES}
                    size="small"
                    value={member.role}
                  />
                ) : (
                  <span className="ws-chip">{roleLabel(member.role)}</span>
                )}
              </span>
              <span className="ws-member-actions">
                {(owner || isMe) && (
                  <Button className="ws-member-action" danger disabled={busy} onClick={() => run(() => removeWorkspaceMember(workspace.id, member.email))} size="small" type="text">
                    {isMe ? 'Leave' : 'Remove'}
                  </Button>
                )}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

WorkspaceMembers.propTypes = {
  workspace: PropTypes.shape({ id: PropTypes.string.isRequired, role: PropTypes.string, owner: PropTypes.object, members: PropTypes.array.isRequired }).isRequired,
  user: PropTypes.shape({ email: PropTypes.string, sub: PropTypes.string }),
  onChange: PropTypes.func.isRequired,
};
